import type {
  AttendanceHistoryItem,
  Collaborator,
  CollaboratorClothing,
  CollaboratorContract,
  CollaboratorUnavailabilityCalendarItem,
  CollaboratorEquipment,
  CollaboratorUnavailability,
  CollaboratorWorkTime,
  HrAnalyticsResponse,
  InterimAgency,
  HrDashboardCoverageResponse,
  HrDashboardOverviewResponse,
  HrDashboardPlanningStatusResponse,
  HrDashboardTrendsResponse,
  HrEffectifDetailResponse,
  HrFraisDetailResponse,
  HrRemoteStorePeriodMetricsResponse,
  HrSalesPeaksResponse,
  HrStoreWeeklyMetricsResponse,
  FetchTransportSegmentsParams,
  NetworkDashboardAvailableStore,
  PlanningConfig,
  PlanningLastOptimization,
  TransportSegmentDisplay,
  WorkShift,
} from '@gozoki/api-types'
import { gozokiApiWithAuth } from '../gozokiApi'

export type { FetchTransportSegmentsParams } from '@gozoki/api-types'

// Fetch all collaborators
export const fetchCollaborators = async (storeId?: number) => {
  const response = await gozokiApiWithAuth.get<Collaborator[]>('/hr/collaborators', {
    params: { storeId },
  })
  return response.data
}

// Fetch one collaborator by id
export const fetchCollaborator = async (id: number) => {
  const response = await gozokiApiWithAuth.get<Collaborator>(`/hr/collaborators/${id}`)
  return response.data
}

// Fetch all work times for a collaborator
export const fetchCollaboratorWorkTimes = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorWorkTime[]>(
    `/hr/collaborators/${collaboratorId}/work-times`,
  )
  return response.data
}

// Fetch all unavailabilities for a collaborator
export const fetchCollaboratorUnavailabilities = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorUnavailability[]>(
    `/hr/collaborators/${collaboratorId}/unavailabilities`,
  )
  return response.data
}

// Fetch all equipment for a collaborator
export const fetchCollaboratorEquipment = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorEquipment[]>(
    `/hr/collaborators/${collaboratorId}/equipment`,
  )
  return response.data
}

// Fetch all clothing for a collaborator
export const fetchCollaboratorClothing = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorClothing[]>(
    `/hr/collaborators/${collaboratorId}/clothing`,
  )
  return response.data
}

// Fetch the contract history for a collaborator (most recent first)
export const fetchCollaboratorContracts = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorContract[]>(
    `/hr/collaborators/${collaboratorId}/contracts`,
  )
  return response.data
}

// Fetch interim agencies (referential for interim contracts)
export const fetchInterimAgencies = async (includeArchived?: boolean) => {
  const response = await gozokiApiWithAuth.get<InterimAgency[]>('/hr/interim-agencies', {
    params: includeArchived ? { includeArchived: 'true' } : undefined,
  })
  return response.data
}

export interface FetchWorkShiftsParams {
  storeId?: number
  collaboratorId?: number
  date?: string
  dateStart?: string
  dateEnd?: string
  clockedOnly?: boolean
  includeClockValues?: boolean
}

export interface FetchAvailableCollaboratorsParams {
  storeId: number
  date: string
  startTime: string
  endTime: string
  excludeWorkShiftId?: number
}

export interface FetchAttendanceHistoryParams {
  storeId: number
}

export interface FetchHrAnalyticsParams {
  storeId?: number
  dateStart?: string
  dateEnd?: string
}

export interface FetchHrSalesPeaksParams {
  storeId: number
}

export interface FetchHrStoreWeeklyMetricsParams {
  storeId: number
  dateStart: string
  dateEnd: string
}

export interface FetchHrRemoteStorePeriodMetricsParams {
  storeId: number
  dateStart: string
  dateEnd: string
}

export interface FetchHrUnavailabilityCalendarParams {
  dateStart: string
  dateEnd: string
  storeId?: number
}

export interface FetchHrDashboardParams {
  dateStart: string
  dateEnd: string
  // Optional store filter; omitted = whole network.
  storeId?: number
  // Multi-sélection (onglet Tableau de bord) — prioritaire sur storeId côté backend.
  storeIds?: number[]
}

// Fetch all work shifts
export const fetchWorkShifts = async (params?: FetchWorkShiftsParams) => {
  const response = await gozokiApiWithAuth.get<WorkShift[]>('/hr/work-shifts', {
    params,
  })
  return response.data
}

// Fetch transport (déplacement payé) display blocks for a store's planning
export const fetchTransportSegments = async (params: FetchTransportSegmentsParams) => {
  const response = await gozokiApiWithAuth.get<TransportSegmentDisplay[]>(
    '/hr/planning/transport-segments',
    { params },
  )
  return response.data
}

// Fetch collaborators available for a shift window
export const fetchAvailableCollaborators = async (params: FetchAvailableCollaboratorsParams) => {
  const response = await gozokiApiWithAuth.get<Collaborator[]>('/hr/collaborator/available', {
    params,
  })
  return response.data
}

export const fetchAttendanceHistory = async (params: FetchAttendanceHistoryParams) => {
  const response = await gozokiApiWithAuth.get<AttendanceHistoryItem[]>('/hr/attendances/history', {
    params,
  })
  return response.data
}

export const fetchHrAnalytics = async (params?: FetchHrAnalyticsParams) => {
  const response = await gozokiApiWithAuth.get<HrAnalyticsResponse>('/hr/analytics', {
    params,
  })
  return response.data
}

export const fetchHrSalesPeaks = async (params?: FetchHrSalesPeaksParams) => {
  const response = await gozokiApiWithAuth.get<HrSalesPeaksResponse>('/hr/analytics/sales-peaks', {
    params,
  })
  return response.data
}

export const fetchHrStoreWeeklyMetrics = async ({
  storeId,
  dateStart,
  dateEnd,
}: FetchHrStoreWeeklyMetricsParams) => {
  const response = await gozokiApiWithAuth.get<HrStoreWeeklyMetricsResponse>(
    `/hr/store-metrics/${storeId}/period`,
    {
      params: {
        dateStart,
        dateEnd,
      },
    },
  )
  return response.data
}

export const fetchHrRemoteStorePeriodMetrics = async ({
  storeId,
  dateStart,
  dateEnd,
}: FetchHrRemoteStorePeriodMetricsParams) => {
  const response = await gozokiApiWithAuth.get<HrRemoteStorePeriodMetricsResponse>(
    `/hr/remote-store-metrics/${storeId}/period`,
    {
      params: {
        dateStart,
        dateEnd,
      },
    },
  )
  return response.data
}

export const fetchHrDashboardOverview = async (params: FetchHrDashboardParams) => {
  const response = await gozokiApiWithAuth.get<HrDashboardOverviewResponse>(
    '/hr/dashboard/overview',
    { params },
  )
  return response.data
}

export const fetchHrDashboardTrends = async (params: FetchHrDashboardParams) => {
  const response = await gozokiApiWithAuth.get<HrDashboardTrendsResponse>('/hr/dashboard/trends', {
    params,
  })
  return response.data
}

export const fetchHrDashboardCoverage = async (params: FetchHrDashboardParams) => {
  const response = await gozokiApiWithAuth.get<HrDashboardCoverageResponse>(
    '/hr/dashboard/coverage',
    { params },
  )
  return response.data
}

export const fetchHrDashboardPlanningStatus = async (params: FetchHrDashboardParams) => {
  const response = await gozokiApiWithAuth.get<HrDashboardPlanningStatusResponse>(
    '/hr/dashboard/plannings-status',
    { params },
  )
  return response.data
}

export const fetchHrEffectifDetail = async (params: FetchHrDashboardParams) => {
  const response = await gozokiApiWithAuth.get<HrEffectifDetailResponse>(
    '/hr/dashboard/effectif-detail',
    { params },
  )
  return response.data
}

export const fetchHrFraisDetail = async (params: FetchHrDashboardParams) => {
  const response = await gozokiApiWithAuth.get<HrFraisDetailResponse>(
    '/hr/dashboard/frais-detail',
    {
      params,
    },
  )
  return response.data
}

export const fetchHrDashboardAvailableStores = async () => {
  const response = await gozokiApiWithAuth.get<NetworkDashboardAvailableStore[]>(
    '/hr/dashboard/available-stores',
  )
  return response.data
}

export const fetchHrUnavailabilityCalendar = async (
  params: FetchHrUnavailabilityCalendarParams,
) => {
  const response = await gozokiApiWithAuth.get<CollaboratorUnavailabilityCalendarItem[]>(
    '/hr/collaborators/unavailabilities/calendar',
    {
      params,
    },
  )
  return response.data
}

// Fetch one work shift by id
export const fetchWorkShift = async (id: number) => {
  const response = await gozokiApiWithAuth.get<WorkShift>(`/hr/work-shifts/${id}`)
  return response.data
}

// Fetch planning configuration (global + per-store)
export const fetchPlanningConfig = async () => {
  const response = await gozokiApiWithAuth.get<PlanningConfig>('/hr/planning/config')
  return response.data
}

export interface FetchLastOptimizationParams {
  dateStart: string
  dateEnd: string
}

export const fetchLastOptimization = async (params: FetchLastOptimizationParams) => {
  const response = await gozokiApiWithAuth.get<{
    lastOptimization: PlanningLastOptimization | null
  }>('/hr/planning/last-optimization', { params })
  return response.data.lastOptimization
}
